/**
 * The places that a person set (nobgp-router#1976): for a node, when the place the router derives
 * from its public address is wrong, and for a cloud location of the org on the map.
 *
 * The router derives a node's place from its address (`node_metadata`), and an address is often
 * registered far from the machine: a Starlink address is placed at its exit point (Benoit's Penngrove
 * nodes were shown in San Jose). A node's controller can set its place, in a table of its own
 * (`node_location_overrides`), and every reader shows that place first.
 *
 * ⚠ The set place goes OVER the derived one here, in the rows the graph and the map read
 * (`useMergedData`). Not in `NodeMetadata`'s type: that type holds exactly the columns the app
 * selects (`nodeMetadataColumns.test.ts`), and a set place is another table.
 *
 * ⚠ This module imports nothing, so its test needs no store and no client.
 */

/** One row of `node_location_overrides`, as the app reads it. */
export interface SetPlace {
  nodeId: string
  latitude: number
  longitude: number
  city: string | null
  state: string | null
  country: string | null
}

/** A place on the map, as `graph/mapPlaces.ts` names it. */
export interface MapPoint {
  lat: number
  lng: number
}

/**
 * The metadata rows with each set place over the derived place. ALL five place fields are replaced,
 * so no derived city is left beside set coordinates; the rest of the row is the node's own.
 *
 * ⚠ The same array when no row has a set place, so a fleet with none draws as it did.
 */
export function withSetPlaces<T extends { nodeId: string }>(rows: T[], places: readonly SetPlace[]): T[] {
  if (!places.length) return rows
  const byNode = new Map(places.map(place => [place.nodeId, place]))
  let changed = false
  const out = rows.map(row => {
    const place = byNode.get(row.nodeId)
    if (!place) return row
    changed = true
    return {
      ...row,
      latitude: place.latitude,
      longitude: place.longitude,
      accuracyRadius: 1,
      // ⚠ A place with no name is shown by its coordinates: the derived city would be wrong.
      city: place.city ?? (place.state || place.country ? null : `${place.latitude.toFixed(2)}, ${place.longitude.toFixed(2)}`),
      state: place.state,
      country: place.country,
    } as T
  })
  return changed ? out : rows
}

/**
 * The set places after a read of the places of the nodes in `asked` (app#489): the places of those
 * nodes come from the answer, the others stay, and only the nodes that the store holds (`held`)
 * keep a place.
 *
 * ⚠ A read asks for the node ids of one moment. A read for a node that came into the store later
 * (`useSetPlaces.getForNodes`) must not be undone by an older full read that did not ask for it,
 * and an answer that comes after its node left the store (an org switch, the offline filter) adds
 * nothing. A row for a node that is not in `asked` is not the answer for it, and is dropped.
 */
export function replacePlaces(
  current: readonly SetPlace[],
  answer: readonly SetPlace[],
  asked: ReadonlySet<string>,
  held: ReadonlySet<string>
): SetPlace[] {
  return [
    ...current.filter(place => !asked.has(place.nodeId) && held.has(place.nodeId)),
    ...answer.filter(place => asked.has(place.nodeId) && held.has(place.nodeId)),
  ]
}

/**
 * The nodes of `asked` that the answer of read number `read` may still change (app#490), and the
 * store then holds that read's answer for them (`answeredBy`).
 *
 * ⚠⚠ THE READ THAT LANDS LAST IS NOT ALWAYS THE NEWER ONE. Two reads can ask for one node: the read
 * for a node that came into the store, and the full read after the reader put it back. When the
 * first one lands last, its answer put back the place that the reader had just cleared. A node is
 * changed only by an answer newer than the one the store holds for it.
 */
export function newerAnswers(asked: readonly string[], read: number, answeredBy: Map<string, number>): Set<string> {
  const newer = new Set(asked.filter(nodeId => (answeredBy.get(nodeId) ?? 0) < read))
  newer.forEach(nodeId => answeredBy.set(nodeId, read))
  return newer
}

/**
 * The name of a place as the app shows it: "Penngrove, CA". The same rule as `formatLocation`: the
 * state, else the country.
 */
export function placeName(place: { city?: string | null; state?: string | null; country?: string | null }): string {
  return [place.city, place.state || place.country].filter(Boolean).join(', ')
}

/**
 * The place a lookup answered for a point or an address (OpenStreetMap Nominatim, `format=jsonv2`
 * with `addressdetails=1`), as the three fields the router stores: the town, the state as its
 * ISO 3166-2 code without the country ("CA"), and the country as its ISO 3166-1 code ("US").
 *
 * ⚠ Never the street or the house number: the router stores the name of a place, not an address
 * (nobgp-router#1976 decision 3), and this is the one place the app turns a lookup into that name.
 */
export function placeFromLookup(answer: unknown): { city: string; state: string; country: string } {
  const address = (answer as { address?: Record<string, unknown> } | null)?.address ?? {}
  const text = (key: string) => (typeof address[key] === 'string' ? (address[key] as string) : '')
  const city = text('city') || text('town') || text('village') || text('hamlet') || text('municipality') || text('county')
  const iso = text('ISO3166-2-lvl4') || text('ISO3166-2-lvl3')
  const state = iso.includes('-') ? iso.slice(iso.indexOf('-') + 1) : text('state')
  return { city, state, country: text('country_code').toUpperCase() }
}

/** The point a lookup answered, or null: Nominatim gives `lat` and `lon` as strings. */
export function pointFromLookup(answer: unknown): MapPoint | null {
  const { lat, lon } = (answer ?? {}) as { lat?: unknown; lon?: unknown }
  const latitude = Number(lat)
  const longitude = Number(lon)
  if (typeof lat !== 'string' || typeof lon !== 'string' || !Number.isFinite(latitude) || !Number.isFinite(longitude)) return null
  if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) return null
  return { lat: latitude, lng: longitude }
}
